import { useState, useRef, useCallback, useEffect } from "react";

// V57.2 — Shared camera hook for tools that use getUserMedia.
// V57.3 — Added permission pre-check via navigator.permissions API.
//         When the browser already has camera permission granted, the
//         hook auto-enables on mount, skipping the manual "Enable camera"
//         pre-flight UI. Falls back gracefully on browsers without the
//         Permissions API (Safari) — those just show the manual button
//         as before.
//
// Solves several mobile-browser pitfalls that were causing the
// pre-V57.2 camera tools to silently fail (no display, no error):
//   1. facingMode "environment" with no fallback rejects on devices
//      with only a front camera. We try "environment" first, then fall
//      back to no constraint, then to "user".
//   2. iOS Safari requires the .play() call to be tied to a user
//      gesture. We surface the actual play() promise so the caller
//      can await it inside the click handler.
//   3. Stream cleanup on unmount — without explicit track.stop() the
//      camera LED stays on after the user navigates away.
//   4. State machine surfaces the real reason for failure
//      (denied / unsupported / insecure / nodevices) so the UI can
//      show an actionable message.
//   5. V57.3 — permission re-prompt fatigue. Even when the browser
//      remembers the grant, we were showing "Tap to enable camera"
//      every time. Now we auto-enable when the browser reports
//      permission already granted.
//
// Usage:
//   const { state, videoRef, enable, disable } = useCamera({ facingMode: "environment" });
//   <button onClick={enable}>Enable camera</button>
//   <video ref={videoRef} playsInline muted />

export function useCamera(opts = {}) {
  const { facingMode = "environment", audio = false, autoEnable = true } = opts;
  const [state, setState] = useState("idle"); // idle | requesting | ok | denied | unsupported | insecure | nodevices
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const enableRef = useRef(null);

  const tryGetMedia = async (constraints) => {
    return await navigator.mediaDevices.getUserMedia(constraints);
  };

  const enable = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setState("unsupported");
      return;
    }
    if (!window.isSecureContext) {
      setState("insecure");
      return;
    }
    setState("requesting");
    let stream = null;
    // Try preferred facingMode first, then unconstrained, then opposite
    const attempts = [
      { video: { facingMode: { ideal: facingMode } }, audio },
      { video: true, audio },
      { video: { facingMode: { ideal: facingMode === "environment" ? "user" : "environment" } }, audio },
    ];
    let lastError = null;
    for (const constraints of attempts) {
      try {
        stream = await tryGetMedia(constraints);
        break;
      } catch (err) {
        lastError = err;
        // NotAllowedError = user denied; don't keep trying
        if (err && err.name === "NotAllowedError") {
          setState("denied");
          return;
        }
        // OverconstrainedError or NotFoundError = try next attempt
      }
    }
    if (!stream) {
      const reason = lastError && lastError.name === "NotFoundError" ? "nodevices" : "denied";
      setState(reason);
      return;
    }
    streamRef.current = stream;
    // V57.6: srcObject attachment moved to a useEffect that watches state.
    // The previous (V57.5) approach assigned srcObject here, but for
    // callers that conditionally render their <video> element (most
    // tools), videoRef.current is null at this point — the element
    // doesn't exist in the DOM yet. The assignment silently no-ops on
    // a null ref. Result: blank screen after permission grant. By
    // moving the assignment to a state-watching useEffect, attachment
    // happens after React mounts the <video> element.
    setState("ok");
  }, [facingMode, audio]);

  // V57.6: attach the stream once both the stream and the video element
  // are ready. Runs after every render that touches `state`.
  useEffect(() => {
    if (state === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [state]);

  // Keep an enable ref so the permission-precheck effect can call the
  // latest enable() without needing it in its dependency array.
  enableRef.current = enable;

  // V57.3 — Auto-enable on mount if browser reports camera permission
  // already granted. Permissions API not available on Safari; in that
  // case the user must tap "Enable camera" as before (same as V57.2).
  useEffect(() => {
    if (!autoEnable) return;
    if (!navigator.permissions || !navigator.permissions.query) return;
    let cancelled = false;
    (async () => {
      try {
        const perm = await navigator.permissions.query({ name: "camera" });
        if (cancelled) return;
        if (perm.state === "granted") {
          // Browser already remembers — auto-start, skip pre-flight UI.
          enableRef.current && enableRef.current();
        }
      } catch (_e) {
        // permissions.query may throw on some browsers; safe to ignore.
      }
    })();
    return () => { cancelled = true; };
  }, [autoEnable]);

  const disable = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setState("idle");
  }, []);

  // Cleanup on unmount — prevent camera LED staying on after navigation.
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  return { state, videoRef, streamRef, enable, disable };
}
